import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "../../lib/api";
import { Input } from "../../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { toast } from "sonner";
import { CenterArtistsPanel } from "../../components/dashboard/CenterArtistsPanel";
import { ArtworkUploadForm } from "../../components/dashboard/ArtworkUploadForm";
import { EditUserDialog } from "./EditUserDialog";

/** Admin — Art Hubs management: publish/unpublish, edit, suspend and remove
 *  hubs; create artists under any hub and upload artworks on their behalf. */
export default function AdminCenters() {
  const [centers, setCenters] = useState([]);
  const [centerId, setCenterId] = useState("");
  const [artists, setArtists] = useState([]);
  const [artistSearch, setArtistSearch] = useState("");
  const [artistId, setArtistId] = useState("");
  const [section, setSection] = useState("artists"); // artists | artworks
  const [editHub, setEditHub] = useState(null);

  const loadCenters = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/art-centers");
      setCenters(data);
    } catch (_e) {
      /* non-blocking */
    }
  }, []);

  useEffect(() => {
    loadCenters();
  }, [loadCenters]);

  const loadArtists = useCallback(async (search) => {
    try {
      const { data } = await api.get("/admin/users", {
        params: { role: "artist", limit: 50, ...(search ? { search } : {}) },
      });
      setArtists(data.items || []);
    } catch (_e) {
      /* non-blocking */
    }
  }, []);

  useEffect(() => {
    if (section !== "artworks") return;
    const t = setTimeout(() => loadArtists(artistSearch.trim()), 300);
    return () => clearTimeout(t);
  }, [section, artistSearch, loadArtists]);

  const selectedArtist = artists.find((a) => a.id === artistId);

  const patchLocal = (id, patch) =>
    setCenters((cs) => cs.map((c) => (c.id === id ? { ...c, ...patch } : c)));

  const publishHub = async (c) => {
    try {
      const { data } = await api.post(`/admin/users/${c.id}/publish`);
      patchLocal(c.id, data);
      toast.success("Hub published · visible on /art-hubs");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Publish failed");
    }
  };

  const unpublishHub = async (c) => {
    if (!window.confirm(`Unpublish ${c.name}? The hub is hidden from the public site but can still sign in.`)) return;
    try {
      const { data } = await api.post(`/admin/users/${c.id}/unpublish`);
      patchLocal(c.id, data);
      toast.success("Hub unpublished · hidden from public site");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Unpublish failed");
    }
  };

  const toggleSuspend = async (c) => {
    const next = !c.suspended;
    if (next && !window.confirm(`Suspend ${c.name}? The hub is hidden AND cannot sign in until reinstated.`)) return;
    try {
      const { data } = await api.patch(`/admin/users/${c.id}`, { suspended: next });
      patchLocal(c.id, data);
      toast.success(next ? "Hub suspended · sign-in blocked" : "Hub reinstated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Update failed");
    }
  };

  const removeHub = async (c) => {
    if (!window.confirm(`Remove ${c.name}? The hub is hidden everywhere and can't sign in. Its data is preserved and this is reversible.`)) return;
    try {
      await api.delete(`/admin/users/${c.id}`);
      toast.success("Hub removed");
      loadCenters();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Remove failed");
    }
  };

  return (
    <div data-testid="admin-centers-page">
      <p className="label-eyebrow text-[#555]">— Admin</p>
      <h1 className="font-serif-display text-4xl tracking-tighter font-light mt-2">Art Hubs</h1>
      <p className="text-sm text-[#555] mt-2 max-w-2xl">
        Publish, edit, suspend or remove hubs. Create artists under any hub and upload works on
        their behalf. Uploads follow the normal review flow (draft until published in Catalogue).
      </p>

      {/* Hubs list */}
      <div className="mt-8 border border-[#E5E0D8] bg-white">
        <div className="px-5 py-3 border-b border-[#E5E0D8] bg-[#F4EFE6]/40">
          <p className="label-eyebrow text-[#555]">Registered hubs ({centers.length})</p>
        </div>
        {centers.map((c) => {
          const isDraft = c.publish_status === "draft";
          const hidden = isDraft || c.suspended;
          return (
            <div
              key={c.id}
              data-testid={`admin-center-row-${c.id}`}
              className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 border-b border-[#E5E0D8] last:border-0"
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <Link to={`/art-hub/${c.id}`} className="font-serif-display text-lg text-[#1E3A8A] hover:underline">
                    {c.name}
                  </Link>
                  {hidden ? (
                    <span data-testid={`hub-hidden-badge-${c.id}`} className="label-eyebrow bg-[#1E3A8A]/10 text-[#1E3A8A] px-2 py-0.5 border border-[#1E3A8A]/30">
                      HIDDEN
                    </span>
                  ) : (
                    <span data-testid={`hub-live-badge-${c.id}`} className="label-eyebrow bg-[#1B5E20]/10 text-[#1B5E20] px-2 py-0.5 border border-[#1B5E20]/30">
                      LIVE
                    </span>
                  )}
                  {c.suspended && (
                    <span className="label-eyebrow text-[#D32F2F]">SUSPENDED</span>
                  )}
                </div>
                <p className="text-xs text-[#555] mt-1">
                  {c.country || "—"} · {c.member_count} member{c.member_count === 1 ? "" : "s"} · {c.approval_status || "approved"}
                </p>
              </div>
              <div className="flex items-center gap-3 flex-wrap justify-end">
                <button
                  onClick={() => setEditHub(c)}
                  data-testid={`hub-edit-${c.id}`}
                  className="label-eyebrow text-[#1E3A8A] hover:text-[#111]"
                >
                  Edit
                </button>
                {isDraft ? (
                  <button
                    onClick={() => publishHub(c)}
                    data-testid={`hub-publish-${c.id}`}
                    className="label-eyebrow text-[#1B5E20] hover:text-[#111]"
                  >
                    Publish
                  </button>
                ) : (
                  <button
                    onClick={() => unpublishHub(c)}
                    data-testid={`hub-unpublish-${c.id}`}
                    className="label-eyebrow text-[#555] hover:text-[#D32F2F]"
                  >
                    Unpublish
                  </button>
                )}
                <button
                  onClick={() => toggleSuspend(c)}
                  data-testid={`hub-suspend-${c.id}`}
                  className="label-eyebrow text-[#1E3A8A] hover:text-[#D32F2F]"
                >
                  {c.suspended ? "Reinstate" : "Suspend"}
                </button>
                <button
                  onClick={() => removeHub(c)}
                  data-testid={`hub-remove-${c.id}`}
                  className="label-eyebrow text-[#D32F2F] hover:underline"
                >
                  Remove
                </button>
              </div>
            </div>
          );
        })}
        {centers.length === 0 && <p className="px-5 py-4 text-sm text-[#555]">No art hubs yet.</p>}
      </div>

      {/* Section tabs */}
      <div className="mt-10 flex items-center gap-2 border-b border-[#E5E0D8]">
        <button
          data-testid="admin-centers-tab-artists"
          onClick={() => setSection("artists")}
          className={`label-eyebrow px-4 py-3 border-b-2 transition-colors ${
            section === "artists" ? "border-[#1E3A8A] text-[#1E3A8A]" : "border-transparent text-[#555] hover:text-[#111]"
          }`}
        >
          Add artist to a center
        </button>
        <button
          data-testid="admin-centers-tab-artworks"
          onClick={() => setSection("artworks")}
          className={`label-eyebrow px-4 py-3 border-b-2 transition-colors ${
            section === "artworks" ? "border-[#1E3A8A] text-[#1E3A8A]" : "border-transparent text-[#555] hover:text-[#111]"
          }`}
        >
          Upload artwork for an artist
        </button>
      </div>

      {section === "artists" && (
        <div className="mt-8">
          <div className="max-w-sm">
            <label className="label-eyebrow text-[#555]">Art hub</label>
            <Select value={centerId} onValueChange={setCenterId}>
              <SelectTrigger
                data-testid="admin-center-select"
                className="mt-2 rounded-none border-b-2 border-l-0 border-r-0 border-t-0 border-[#E5E0D8] bg-transparent px-0 focus:ring-0"
              >
                <SelectValue placeholder="Choose a hub…" />
              </SelectTrigger>
              <SelectContent className="rounded-none">
                {centers.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {centerId && (
            <div className="mt-6">
              <CenterArtistsPanel
                key={centerId}
                members={[]}
                createFn={(payload) => api.post("/admin/center-artists", { ...payload, center_id: centerId })}
              />
            </div>
          )}
        </div>
      )}

      {section === "artworks" && (
        <div className="mt-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl">
            <div>
              <label className="label-eyebrow text-[#555]">Search artists</label>
              <Input
                data-testid="admin-artist-search"
                value={artistSearch}
                onChange={(e) => setArtistSearch(e.target.value)}
                placeholder="Name or email…"
                className="mt-2 rounded-none border-b-2 border-l-0 border-r-0 border-t-0 border-[#E5E0D8] bg-transparent px-0 py-2 focus-visible:ring-0 focus-visible:border-[#111]"
              />
            </div>
            <div>
              <label className="label-eyebrow text-[#555]">Artist</label>
              <Select value={artistId} onValueChange={setArtistId}>
                <SelectTrigger
                  data-testid="admin-artist-select"
                  className="mt-2 rounded-none border-b-2 border-l-0 border-r-0 border-t-0 border-[#E5E0D8] bg-transparent px-0 focus:ring-0"
                >
                  <SelectValue placeholder="Choose an artist…" />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  {artists.map((a) => (
                    <SelectItem key={a.id} value={a.id}>
                      {a.name}{a.email ? ` — ${a.email}` : " (managed)"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          {selectedArtist && (
            <ArtworkUploadForm
              key={artistId}
              user={selectedArtist}
              submitFn={(payload) => api.post("/admin/artworks", { ...payload, artist_id: artistId })}
            />
          )}
        </div>
      )}

      <EditUserDialog
        user={editHub}
        open={!!editHub}
        onClose={() => setEditHub(null)}
        onUpdated={(updated) => {
          setCenters((cs) => cs.map((c) => (c.id === updated.id ? { ...c, ...updated } : c)));
          setEditHub(null);
        }}
      />
    </div>
  );
}
